/* Showtime Pools Equipment — progressive enhancement. Every feature works
   without this file; it only adds motion, live search and convenience. */
(() => {
  window.ShowtimeTheme = true;

  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  /* Motion: split headings into words, reveal on scroll */
  const splitWords = (el) => {
    if (el.classList.contains('is-split')) return;
    let i = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part));
              return;
            }
            const word = document.createElement('span');
            word.className = 'word';
            const inner = document.createElement('span');
            inner.textContent = part;
            inner.style.setProperty('--i', i++);
            word.appendChild(inner);
            frag.appendChild(word);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'BR') {
          walk(child);
        }
      });
    };
    walk(el);
    el.classList.add('is-split');
  };

  let revealObserver;
  const initMotion = (scope = document) => {
    if (!root.classList.contains('motion-ready')) return;
    scope.querySelectorAll('.split-words').forEach(splitWords);
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-inview');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );
    }
    const aboveFold = [];
    scope.querySelectorAll('.reveal, .split-words, .sheen').forEach((el) => {
      if (el.classList.contains('is-inview')) return;
      if (el.getBoundingClientRect().top < window.innerHeight) aboveFold.push(el);
      else revealObserver.observe(el);
    });
    requestAnimationFrame(() => requestAnimationFrame(() => aboveFold.forEach((el) => el.classList.add('is-inview'))));
  };

  /* Header: elevation on scroll */
  const header = document.querySelector('[data-site-header]');
  if (header) {
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  /* Disclosures: header search and nav dropdowns */
  const closeDetails = (details, focusSummary) => {
    if (!details || !details.open) return;
    details.open = false;
    if (focusSummary) details.querySelector('summary')?.focus();
  };

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open) return;
      if (details.matches('[data-nav-dropdown]')) {
        document.querySelectorAll('[data-nav-dropdown][open]').forEach((d) => d !== details && closeDetails(d));
      }
      if (details.matches('[data-header-search]')) {
        document.querySelectorAll('[data-nav-dropdown][open]').forEach((d) => closeDetails(d));
        requestAnimationFrame(() => details.querySelector('[data-autofocus]')?.focus());
      }
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('[data-header-search][open], [data-nav-dropdown][open]');
    if (open && open.contains(document.activeElement)) closeDetails(open, true);
  });

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-header-search][open], [data-nav-dropdown][open]').forEach((d) => {
      if (!d.contains(event.target)) closeDetails(d);
    });
  });

  /* Drawer (native dialog) */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.drawerOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
        opener.setAttribute('aria-expanded', 'true');
        dialog.addEventListener('close', () => opener.setAttribute('aria-expanded', 'false'), { once: true });
      }
      return;
    }
    if (event.target.closest('[data-drawer-close]')) {
      event.target.closest('dialog')?.close();
      return;
    }
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('drawer')) {
      event.target.close();
    }
  });

  /* Predictive search (combobox) */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-enabled') || this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-results]');
      this.status = this.querySelector('[data-status]');
      if (!this.input || !this.results) return;
      this.active = -1;
      this.input.addEventListener('input', debounce(() => this.search(), 220));
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.input.addEventListener('focus', () => {
        if (this.results.innerHTML.trim() && this.input.value.trim()) this.open();
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.close();
      });
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.close();
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        section_id: 'predictive-search',
      });
      try {
        const res = await fetch(`${this.dataset.url}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const panel = doc.querySelector('.ps');
        if (!panel) return;
        this.results.innerHTML = panel.outerHTML;
        this.options = [...this.results.querySelectorAll('[role="option"]')];
        this.options.forEach((opt, i) => (opt.id = `${this.input.id}-opt-${i}`));
        this.active = -1;
        this.input.removeAttribute('aria-activedescendant');
        if (this.status) this.status.textContent = panel.dataset.count ? `${panel.dataset.count}` : '';
        this.open();
      } catch (err) {
        if (err.name !== 'AbortError') this.close();
      }
    }

    onKey(e) {
      if (e.key === 'Escape') {
        if (!this.results.hidden) {
          e.stopPropagation();
          this.close();
        }
        return;
      }
      if (!this.options || this.results.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const dir = e.key === 'ArrowDown' ? 1 : -1;
        this.active = (this.active + dir + this.options.length) % this.options.length;
        this.options.forEach((opt, i) => opt.setAttribute('aria-selected', i === this.active ? 'true' : 'false'));
        const current = this.options[this.active];
        this.input.setAttribute('aria-activedescendant', current.id);
        current.scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && this.active > -1) {
        e.preventDefault();
        this.options[this.active].querySelector('a')?.click();
      }
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (!this.results) return;
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* Product: variant picker */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variants]');
      if (!data) return;
      this.variants = JSON.parse(data.textContent);
      this.section = this.closest('[data-product-section]');
      this.addEventListener('change', () => this.onChange());
    }

    onChange() {
      const fieldsets = [...this.querySelectorAll('fieldset')];
      const selected = fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
      fieldsets.forEach((fs, i) => {
        const label = fs.querySelector('[data-selected-value]');
        if (label) label.textContent = selected[i] || '';
      });
      const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      const submit = this.section.querySelector('[data-variant-update="submit"] button');
      if (!variant) {
        if (submit) {
          submit.disabled = true;
          submit.textContent = this.dataset.unavailable || '';
        }
        return;
      }
      const idInput = this.section.querySelector('[data-variant-id]');
      if (idInput) idInput.value = variant.id;
      if (submit) submit.disabled = !variant.available;
      const url = `${this.dataset.url}?variant=${variant.id}`;
      window.history.replaceState({}, '', url);
      if (variant.featured_media) gallerySelect(this.section, variant.featured_media.id);
      this.refresh(variant.id);
    }

    async refresh(variantId) {
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const res = await fetch(`${this.dataset.url}?variant=${variantId}&section_id=${this.dataset.section}`, {
          signal: this.controller.signal,
        });
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        ['price', 'ids', 'availability', 'submit'].forEach((key) => {
          const src = doc.querySelector(`[data-variant-update="${key}"]`);
          const dst = this.section.querySelector(`[data-variant-update="${key}"]`);
          if (src && dst) dst.innerHTML = src.innerHTML;
        });
        doc.querySelectorAll('.variant-pill').forEach((pill) => {
          const mine = this.querySelector(`label[for="${pill.getAttribute('for')}"]`);
          if (mine) mine.className = pill.className;
        });
      } catch (err) {
        if (err.name !== 'AbortError') window.location.href = `${this.dataset.url}?variant=${variantId}`;
      }
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* Product: gallery */
  const gallerySelect = (scope, mediaId) => {
    const slide = scope.querySelector(`[data-media-id="${mediaId}"]`);
    const track = slide?.closest('[data-gallery-track]');
    if (!slide || !track) return;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  };

  const initGallery = (scope = document) => {
    scope.querySelectorAll('[data-gallery]').forEach((gallery) => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const track = gallery.querySelector('[data-gallery-track]');
      const thumbs = [...gallery.querySelectorAll('[data-thumb]')];
      thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => {
          const slide = document.getElementById(thumb.dataset.thumb);
          if (slide) gallerySelect(gallery, slide.dataset.mediaId);
        })
      );
      if (!thumbs.length || !track) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            thumbs.forEach((t) =>
              t.dataset.thumb === entry.target.id ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')
            );
          });
        },
        { root: track, threshold: 0.6 }
      );
      track.querySelectorAll('.gallery__slide').forEach((s) => io.observe(s));
    });
  };

  /* Quantity steppers */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-qty]');
    if (!btn) return;
    const input = btn.parentElement.querySelector('input[type="number"]');
    if (!input) return;
    const min = Number(input.min || 1);
    const next = Number(input.value || min) + (btn.dataset.qty === 'plus' ? 1 : -1);
    input.value = Math.max(min, next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Product form: add to cart via the Cart API so Shopify's own error message is
     shown inline. Submits the form's selected variant id and quantity unchanged.
     On success it goes to the cart page (same as the native form); if fetch
     itself fails, the form falls back to a normal submit. */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || !window.fetch || !window.FormData) return;
    event.preventDefault();
    const btn = form.querySelector('[type="submit"][name="add"]');
    const errorEl = form.querySelector('[data-cart-error]');
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }
    let res;
    let data = {};
    try {
      res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      data = await res.json().catch(() => ({}));
    } catch (err) {
      form.submit();
      return;
    }
    if (res.ok) {
      window.location.href = `${root}cart`;
      return;
    }
    if (btn) {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
    if (errorEl) {
      errorEl.textContent = data.description || data.message || errorEl.dataset.generic || '';
      errorEl.hidden = false;
    }
  });

  /* Filters and sorting */
  const initFacets = (scope = document) => {
    const desktop = window.matchMedia('(min-width: 1024px)');
    scope.querySelectorAll('[data-facets]').forEach((details) => {
      if (desktop.matches) details.open = true;
    });
  };
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-auto-submit]');
    if (!input) return;
    const form = input.closest('form');
    if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
  });

  /* Cart quantity auto-update */
  const submitCart = debounce((form) => {
    const updateBtn = form.querySelector('[data-cart-update]');
    form.requestSubmit ? form.requestSubmit(updateBtn) : form.submit();
  }, 500);
  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-cart-qty]')) return;
    submitCart(event.target.form);
  });

  /* Product recommendations */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded || !this.dataset.url) return;
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          io.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      io.observe(this);
    }

    async load() {
      this.dataset.loaded = 'true';
      try {
        const res = await fetch(this.dataset.url);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) {
          this.innerHTML = fresh.innerHTML;
          this.className = fresh.className;
          initMotion(this);
        }
      } catch (err) {
        /* Recommendations are optional; fail silently. */
      }
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* Init + Theme Editor support */
  const init = (scope = document) => {
    initMotion(scope);
    initGallery(scope);
    initFacets(scope);
  };
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
